import { memo, useMemo } from 'react'

import { ColDef } from 'ag-grid-community'
import { add, format as formatDate, isValid, sub } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { isBoolean } from 'lodash'

import {
  DateCellEditor,
  DealTeamCellEditor,
  DelayReasonCellEditor,
  MultiSelectOptionsEditor,
  StageSelectorCellEditor,
  useWQValueSetters,
} from './CellEditors'
import { OpsDelayReasonCellEditor } from './CellEditors/OpsDelayReasonCellEditor'
import {
  ApplicationLinkRenderer,
  ApplicationStateRenderer,
  DealTeamRenderer,
  DealTeamToolTip,
} from './CellRenderers'
import { HtmlRenderer } from './CellRenderers/HtmlRenderer'
import { HtmlTooltip } from './CellRenderers/HtmlTooltip'
import { HubSpotLinkRenderer } from './CellRenderers/HubSpotLink'
import { AlertsColumnRenderer } from './CellRenderers/alerts/AlertsColumnRenderer'
import { AlertsColumnTooltip } from './CellRenderers/alerts/AlertsColumnTooltip'
import { defaultDelayReasons } from '@/constants/delayReasons'
import { PROPERTY_USAGES, PR_REVIEW_STATUSES } from '@/constants/globals'
import {
  appraisalTypeOptions,
  compDistanceFromSubjPropOptions,
  compSaleDateOptions,
  propertyNeighborhoodBuiltUpRangeTypeOptions,
  propertyNeighborhoodGrowthPaceTypeOptions,
  propertyNeighborhoodLocationTypeOptions,
  siteZoningComplianceTypeOptions,
  useOfProceedsOptions,
} from '@/features/application/applicationSlice'
import { useGetWorkQueuesFieldsQuery } from '@/services/application'
import { useGetLoggedUserQuery } from '@/services/user'
import { PipelineApplication } from '@/types/unlock'
import { formatCurrency } from '@/utils'
import { isUnderwritingUser, isValuationUser } from '@/utils/user'

const textFilterOptions = [
  'contains',
  'notContains',
  'equals',
  'notEqual',
  'startsWith',
  'endsWith',
  'blank',
  'notBlank',
]
const numberFilterOptions = [
  'equals',
  'notEqual',
  'lessThan',
  'greaterThan',
  'greaterThanOrEqual',
  'lessThanOrEqual',
  'inRange',
  'blank',
  'notBlank',
]
const dateFilterOptions = [
  'equals',
  'notEqual',
  'lessThan',
  'greaterThan',
  'inRange',
  'blank',
  'notBlank',
]

function dateFormatter(params, dateFormat = 'MMM dd yyyy', utc = false) {
  try {
    const value = params.data?.[params.colDef.field]?.substring(0, 19)
    let dateValue = new Date(value)
    if (!isValid(dateValue)) return '-'

    if (utc) {
      dateValue = utcToZonedTime(dateValue, 'UTC')
    }

    // if the time is present in the date, adjust the timezone
    // else add midnight time to avoid timezone changes by new Date()
    if (value.length === 19) {
      const timezoneOffset = new Date().getTimezoneOffset()
      dateValue = sub(dateValue, { minutes: Number(timezoneOffset) })
    } else {
      dateValue = new Date(`${value} 00:00:00`)
    }
    return value ? formatDate(dateValue, dateFormat) : '-'
  } catch (error) {
    console.error('Error formatting date:', error, params)
    return '-'
  }
}

function dateTimeFormatter(params) {
  return dateFormatter(params, 'MMM dd yyyy hh:mm a') // e.g. Oct 20 2023 12:00 AM
}

function applicantsFormatter({ value }) {
  const mapping = {
    ca: 'CA',
    co_owner: 'CoO',
    non_owning_occupant: 'NOO',
  }
  return value?.map((param) => mapping[param] || param).join(', ')
}

export function useWQGrid() {
  const getRowId = useMemo(() => (params) => params.data.application_uuid, [])
  const {
    applicationValueSetter,
    titleReportValueSetter,
    valuationValueSetter,
    appraisalReportSetter,
    propertyValueSetter,
  } = useWQValueSetters()
  const { data: workQueuesFields, isLoading } = useGetWorkQueuesFieldsQuery()
  const { data: loggedUser } = useGetLoggedUserQuery()

  const defaultColDef: ColDef = {
    resizable: true,
    initialWidth: 128,
    filterParams: {
      suppressAndOrCondition: true,
      debounceMs: 600,
      buttons: ['clear'],
    },
    cellClass: 'text-center',
    initialHide: true,
    sortable: true,
  }

  const delayReasonsOptions = defaultDelayReasons.map((reason) => ({
    value: reason,
    label: reason,
  }))

  const columnDefs: ColDef<PipelineApplication>[] = useMemo(() => {
    const fieldLookup: Record<string, ColDef> = {
      name: {
        initialWidth: 384,
        filter: 'agTextColumnFilter',
        sortable: true,
        pinned: 'left',
        cellRenderer: memo(ApplicationLinkRenderer),
        editable: true,
        cellClass: 'text-left',
        valueFormatter: (params) => params.value || '-',
        valueSetter: applicationValueSetter,
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      alerts_summary: {
        cellRenderer: memo(AlertsColumnRenderer),
        filter: 'agTextColumnFilter',
        filterParams: {},
        cellClass: 'flex items-center justify-center h-full w-full',
        tooltipField: 'alerts_summary',
        tooltipComponent: AlertsColumnTooltip,
        sortable: false,
      },
      deal_teams: {
        cellRenderer: DealTeamRenderer,
        editable: true,
        cellEditorPopup: true,
        cellStyle: { textAlign: 'left' },
        // Suppress enter key to prevent the grid from closing the editor
        suppressKeyboardEvent: (params) => {
          const { event } = params
          if (event.key === 'Enter' || event.key === 'Tab') {
            return true
          }
        },
        cellEditor: DealTeamCellEditor,
        tooltipField: 'deal_teams',
        tooltipComponent: DealTeamToolTip,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: { filterOptions: ['in', 'blank', 'notBlank'] },
      },
      application_state_modified_at: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      modified_at: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      estimated_close_on: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        editable: true,
        cellEditor: DateCellEditor,
        valueSetter: applicationValueSetter,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      application_state: {
        cellRenderer: memo(ApplicationStateRenderer),
        editable: true,
        cellEditorPopup: true,
        cellStyle: { textAlign: 'left' },
        // Suppress enter key to prevent the grid from closing the editor
        suppressKeyboardEvent: (params) => {
          const { event } = params
          if (event.key === 'Enter' || event.key === 'Tab') {
            return true
          }
        },
        cellEditor: StageSelectorCellEditor,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['in'],
        },
        initialWidth: 224,
      },
      delay_reasons: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
        cellClass: 'text-center',
        editable: true,
        cellEditor: DelayReasonCellEditor,
        cellEditorPopup: true,
        cellEditorParams: {
          isMulti: true,
          options: delayReasonsOptions,
        },
        valueSetter: applicationValueSetter,
        tooltipField: 'delay_reasons',
      },
      ops_delay_reasons: {
        cellRenderer: memo(HtmlRenderer),
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
        cellClass: 'text-center',
        editable: true,
        cellEditor: OpsDelayReasonCellEditor,
        cellEditorPopup: true,
        valueSetter: applicationValueSetter,
        tooltipField: 'ops_delay_reasons',
        tooltipComponent: HtmlTooltip,
        suppressKeyboardEvent: (params) => {
          const { event } = params
          if (event.key === 'Enter' || event.key === 'Tab') {
            return true
          }
        },
      },
      requested_investment: {
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
        valueFormatter: (params) => (params.value ? formatCurrency({ amount: params.value }) : '-'),
        sortable: true,
        editable: true,
        valueSetter: applicationValueSetter,
        width: 128,
        cellClass: 'text-right',
      },
      title_ordered: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        editable: true,
        cellEditor: DateCellEditor,
        valueSetter: titleReportValueSetter,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      title_pr_date: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      title_pr_status: {
        valueFormatter: (params) => PR_REVIEW_STATUSES[params.value],
        cellClass: 'text-left',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      title_received: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        editable: true,
        cellEditor: DateCellEditor,
        valueSetter: titleReportValueSetter,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      appraisal_ordered: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        cellEditor: DateCellEditor,
        valueSetter: valuationValueSetter,
        // Allow edits only if there's a valuation
        editable: (params) => !!params.data.appraisal_ordered,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      inspection_scheduled: {
        headerName: 'Insp Scheduled',
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        cellEditor: DateCellEditor,
        valueSetter: appraisalReportSetter,
        editable: true,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      valuation_report_expected: {
        headerName: 'Val Rpt Expected',
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        cellEditor: DateCellEditor,
        valueSetter: appraisalReportSetter,
        editable: true,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      appraisal_received: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        cellEditor: DateCellEditor,
        valueSetter: valuationValueSetter,
        // Allow edits only if there's a valuation
        editable: (params) => !!params.data.appraisal_received,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      appraisal_review_status: {
        cellEditor: MultiSelectOptionsEditor,
        cellClass: 'text-center',
        cellEditorParams: {
          isMulti: false,
          options: [
            { value: 'approved', label: 'Approved' },
            { value: 'in-process', label: 'In Process' },
            { value: 'issue-found', label: 'Issue Found' },
            { value: 'recon-status', label: 'Recon Status' },
          ],
        },
        cellEditorPopup: true,
        valueSetter: valuationValueSetter,
        // Allow edits only if there's a valuation
        editable: (params) => !!params.data.appraisal_review_status,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
        valueFormatter: (params) => params.value || '-',
      },
      property_neighborhood_location_type: {
        valueFormatter: (params) =>
          params.value
            ? propertyNeighborhoodLocationTypeOptions?.find(
                (option) => option.value === params.value
              )?.label
            : '-',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [
            'Blank', // Ensure 'Blank' is the first element
            ...propertyNeighborhoodLocationTypeOptions.map((option) => option.label),
          ],
        },
      },
      property_neighborhood_built_up_range_type: {
        valueFormatter: (params) =>
          params.value
            ? propertyNeighborhoodBuiltUpRangeTypeOptions?.find(
                (option) => option.value === params.value
              )?.label
            : '-',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [
            'Blank',
            ...propertyNeighborhoodBuiltUpRangeTypeOptions.map((option) => option.label),
          ],
        },
      },
      property_neighborhood_growth_pace_type: {
        valueFormatter: (params) =>
          params.value
            ? propertyNeighborhoodGrowthPaceTypeOptions?.find(
                (option) => option.value === params.value
              )?.label
            : '-',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [
            'Blank',
            ...propertyNeighborhoodGrowthPaceTypeOptions.map((option) => option.label),
          ],
        },
      },
      comparable_sales_recency: {
        valueFormatter: (params) =>
          params.value
            ? compSaleDateOptions?.find((option) => option.value === params.value)?.label
            : '-',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['Blank', ...compSaleDateOptions.map((option) => option.label)],
        },
      },
      comparable_farther_than_mile: {
        valueFormatter: (params) =>
          isBoolean(params.value)
            ? compDistanceFromSubjPropOptions?.find((option) => option.value === params.value)
                ?.label
            : '-',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['Blank', ...compDistanceFromSubjPropOptions.map((option) => option.label)],
        },
      },
      flood_zone_hazard_indicator: {
        valueFormatter: (params) =>
          isBoolean(params.value)
            ? [
                { value: false, label: 'No' },
                { value: true, label: 'Yes' },
              ].find((option) => option.value === params.value)?.label
            : '-',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [
            'Blank',
            ...[
              { value: false, label: 'No' },
              { value: true, label: 'Yes' },
            ].map((option) => option.label),
          ],
        },
      },
      site_zoning_compliance_type: {
        valueFormatter: (params) =>
          params.value
            ? siteZoningComplianceTypeOptions?.find((option) => option.value === params.value)
                ?.label
            : '-',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['Blank', ...siteZoningComplianceTypeOptions.map((option) => option.label)],
        },
      },
      appraisal_review_status_date: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        cellEditor: DateCellEditor,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      application_created_at: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        cellEditor: DateCellEditor,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      automated_exceptions: {
        valueFormatter: (params) => params.value?.map((it) => it.exception_label).join(', '),
        cellClass: 'text-center',
        filter: 'agTextColumnFilter',
        sortable: true,
        filterParams: {
          filterOptions: ['contains'],
        },
      },
      automated_flags: {
        valueFormatter: (params) => params.value?.map((it) => it.exception_label).join(', '),
        cellClass: 'text-center',
        filter: 'agTextColumnFilter',
        sortable: true,
        filterParams: {
          filterOptions: ['contains'],
        },
      },
      insurance_ordered: {
        colId: 'insurance_ordered',
        cellClass: 'text-center',
        editable: true,
        cellEditor: DateCellEditor,
        valueGetter: (params) => params?.data?.property?.insurance_ordered,
        valueSetter: propertyValueSetter,
        valueFormatter: (params) => {
          const value = params?.data?.property?.insurance_ordered
          const dateFormat = 'MMM dd yyyy'
          return value ? formatDate(add(new Date(value), { hours: 12 }), dateFormat) : ''
        },
      },
      insurance_received: {
        colId: 'insurance_received',
        cellClass: 'text-center',
        editable: true,
        cellEditor: DateCellEditor,
        valueGetter: (params) => params?.data?.property?.insurance_received,
        valueSetter: propertyValueSetter,
        valueFormatter: (params) => {
          const value = params?.data?.property?.insurance_received
          const dateFormat = 'MMM dd yyyy'
          return value ? formatDate(add(new Date(value), { hours: 12 }), dateFormat) : ''
        },
      },
      uw_submitted_at: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      hubspot_contact_id: {
        cellRenderer: memo(HubSpotLinkRenderer),
        width: 116,
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      is_mtg_inquires: {
        valueFormatter: (params) => params.value ?? '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      roof_solar_panel: {
        type: 'booleanCheckColumn',
        // valueFormatter: (params) => (params.value !== null ? params.value : '-'),
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      report_ordered_at: {
        valueFormatter: (params) => dateFormatter(params, 'MMM dd yyyy', true),
        cellClass: 'text-center',
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      report_expires_at: {
        valueFormatter: (params) => dateFormatter(params, 'MMM dd yyyy', true),
        cellClass: 'text-center',
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      title_report_effective_date: {
        cellClass: 'text-center',
        valueFormatter: dateFormatter,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      title_report_expiration_date: {
        cellClass: 'text-center',
        valueFormatter: dateFormatter,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      appraisal_effective_date: {
        cellClass: 'text-center',
        valueFormatter: dateFormatter,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      appraisal_expiration_date: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
      },
      property_usage: {
        valueFormatter: (params) => PROPERTY_USAGES[params.value],
        cellClass: 'text-left',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      property_type: {
        cellClass: 'text-left',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      county: {
        cellClass: 'text-left',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      use_of_proceeds: {
        valueFormatter: (params) => {
          if (!params.value) {
            return '-'
          }
          return params.value
            .map(
              (useOfProceed) =>
                useOfProceedsOptions.find((option) => option.value === useOfProceed)?.label
            )
            .join(', ')
        },
        cellClass: 'text-left',
      },
      loss_type: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      loss_reason: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      appraised_home_value: {
        type: 'currencyColumn',
        cellClass: 'text-right',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      avm_variance: {
        type: 'percentColumn',
        valueFormatter: (params) => {
          return params.value && params.value !== null
            ? `${Math.sign(params.value) === 1 ? '+' : ''}${(params.value * 1)?.toFixed(1)}%`
            : '-'
        },
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      application_party_name: {
        initialWidth: 384,
        sortable: true,
        cellClass: 'text-left',
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      approved_escalations: {
        initialWidth: 384,
        filter: 'agTextColumnFilter',
        sortable: true,
        filterParams: {
          filterOptions: ['contains'],
        },
        cellClass: 'text-left',
      },
      follow_up: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        editable: true,
        cellEditor: DateCellEditor,
        valueSetter: applicationValueSetter,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      call_scheduled: {
        valueFormatter: dateTimeFormatter,
        cellClass: 'text-center',
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      cltv: {
        type: 'percentColumn',
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      val_express: {
        cellEditor: MultiSelectOptionsEditor,
        cellClass: 'text-center',
        cellEditorParams: {
          isMulti: false,
          options: [
            { value: true, label: 'true' },
            { value: false, label: 'false' },
          ],
        },
        cellEditorPopup: true,
        valueSetter: valuationValueSetter,
        editable: isValuationUser(loggedUser) || isUnderwritingUser(loggedUser),
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['equals', 'blank', 'notBlank'],
        },
        valueFormatter: (params) => params.value ?? '-',
      },
      wire_to_escrow_on: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        editable: false,
        cellEditor: DateCellEditor,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      kickback: {
        hide: true,
        valueFormatter: 'x.created_at',
      },
    }

    // Show all explicitly declared columns in fieldLookup
    for (const key in fieldLookup) {
      fieldLookup[key].hide = false
    }

    const extraFields = {
      estimated_value_with_overrides: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      calc_application_fico: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      investment_payment: {
        type: 'currencyColumn',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      investment_percentage: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      exchange_rate: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      unlock_percentage: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      home_finance_limit: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      total_payoffs: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      final_net_closing_proceeds: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      exchange_rate_adjustment: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      adjusted_exchange_rate: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      fico_override: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      home_value_override: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      min_investment_override: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      annualized_cost_limit_calc_version: {
        valueSetter: applicationValueSetter,
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
        editable: !!loggedUser?.is_superuser,
      },
      subject_property_address: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      subject_property_state: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      unit_number: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      assessor_parcel_number: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      title_vesting: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      title_held: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      monthly_cost_hoa: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      first_name: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      last_name: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      marital_status: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      mailing_address: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      state: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      zip_code: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      automated_review_status: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      settlement_agent: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      file_number: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      legal_description: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      taxes: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      appraisal_type: {
        valueFormatter: (params) =>
          params.value
            ? appraisalTypeOptions?.find((option) => option.value === params.value)?.label
            : '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      property_condition: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      appraisal_expiration_date: {
        valueFormatter: dateFormatter,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      city: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      external_id: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      agreement_effective_on: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        editable: !!loggedUser?.groups?.some((g) => g.name === 'closing'),
        cellEditor: DateCellEditor,
        valueSetter: applicationValueSetter,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      call_completed: {
        valueFormatter: dateFormatter,
        cellClass: 'text-center',
        editable: true,
        cellEditor: DateCellEditor,
        valueSetter: applicationValueSetter,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: dateFilterOptions,
          buttons: ['reset', 'apply'],
        },
      },
      task_audit_status: {
        cellClass: 'text-center',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      applicants: {
        cellClass: 'text-center',
        valueFormatter: applicantsFormatter,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
      pricing_version: {
        headerName: 'Pricing Version',
        minWidth: 156,
        hide: false,
        editable: () => loggedUser?.is_superuser ?? false,
        valueSetter: applicationValueSetter,
        filter: 'agNumberColumnFilter',
      },
      max_investment_amount: {
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
        valueFormatter: (params) => (params.value ? formatCurrency({ amount: params.value }) : '-'),
        sortable: true,
        width: 128,
        cellClass: 'text-right',
      },
      gross_living_area: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      lot_size: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      year_built: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      gross_adjustment: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      net_adjustment: {
        valueFormatter: (params) => params.value || '-',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: numberFilterOptions,
        },
      },
      site_zoning_classification_identifier: {
        cellClass: 'text-center',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: textFilterOptions,
        },
      },
    }

    return workQueuesFields
      ?.filter((field) => field.section) // Filter out fields that don't have a section
      .map(({ value, label, ...extraParams }): ColDef => {
        return {
          headerName: label,
          field: value,
          ...extraParams,
          ...fieldLookup[value],
          ...extraFields[value],
        }
      })
  }, [workQueuesFields, loggedUser])

  return {
    getRowId,
    defaultColDef,
    columnDefs,
    isLoading,
  }
}
